import { useFetchJSON } from "@/db/dbManag";
import { API } from "@/enums/endpoint";
import { DisciplineResponse, UsernameResponse } from "@/types/app";
import { defineStore } from "pinia";
import { ref } from "vue";

export const useAppStore = defineStore("appStore", () => {
  const discipline = ref<DisciplineResponse[] | null>(null);
  const userInfo = ref<UsernameResponse | null>(null);

  const setDiscipline = (value: DisciplineResponse[] | null) => {
    discipline.value = value;
  };

  const setUserInfo = (value: UsernameResponse | null) => {
    userInfo.value = value;
  };

  const getAllDiscipline = async () => {
    const { data, execute } = useFetchJSON(API.POST.LIST.DISCIPLINE).post({}).json();

    await execute();

    discipline.value = data.value?.results || null;
  };

  // Carica la lista discipline una sola volta (cache nello store) e restituisce la disciplina testuale
  // corrispondente all'id, secondo la fonte di verità /v1/Lookup/Discipline/list.
  const getDisciplinaById = async (id: number | string | null | undefined): Promise<string | null> => {
    if (id === null || id === undefined || id === "") return null;
    if (!discipline.value) await getAllDiscipline();
    return discipline.value?.find(d => d.id === Number(id))?.disciplina ?? null;
  };

  // Normalizza un valore disciplina nel NOME CANONICO del lookup /v1/Lookup/Discipline/list,
  // indipendentemente dal formato di origine (il campo Discipline può arrivare come id numerico
  // oppure come nome testuale, anche in forma breve es. "Beach"):
  // - id numerico ("0", 1, "2")   -> match per id
  // - nome testuale ("Beach")     -> normalizzato al nome canonico ("Beach Tennis") via match
  //   esatto case-insensitive e, in mancanza, match parziale (contiene/è contenuto).
  // Fallback: il valore originale (se nessun match nel lookup).
  const resolveDisciplina = async (value: number | string | null | undefined): Promise<string | null> => {
    if (value === null || value === undefined || value === "") return null;
    if (!Number.isNaN(Number(value))) return (await getDisciplinaById(value)) ?? null;

    if (!discipline.value) await getAllDiscipline();
    const list = discipline.value ?? [];
    const v = String(value).trim().toLowerCase();
    // 1) match esatto (case-insensitive)
    const exact = list.find(d => d.disciplina.toLowerCase() === v);
    if (exact) return exact.disciplina;
    // 2) match parziale: il nome canonico contiene il valore o viceversa (es. "Beach" -> "Beach Tennis")
    const partial = list.find(d => d.disciplina.toLowerCase().includes(v) || v.includes(d.disciplina.toLowerCase()));
    if (partial) return partial.disciplina;
    // 3) fallback: valore originale
    return String(value);
  };

  // Resolver sincrono e reattivo per il display: legge la cache (precaricata in App.vue) e
  // restituisce la disciplina testuale dell'id, secondo /v1/Lookup/Discipline/list.
  const disciplinaName = (id: number | string | null | undefined): string => {
    if (id === null || id === undefined || id === "") return "";
    return discipline.value?.find(d => d.id === Number(id))?.disciplina ?? "";
  };

  // Variante per stringhe con più id ("0,2,3" o "0;2;3") -> "Tennis, Padel, Pickleball".
  const disciplinaNames = (ids: string | null | undefined): string => {
    if (!ids) return "";
    return String(ids)
      .split(/[;,]/)
      .map(id => disciplinaName(id.trim()))
      .filter(Boolean)
      .join(", ");
  };

  return {
    discipline,
    userInfo,
    getAllDiscipline,
    getDisciplinaById,
    resolveDisciplina,
    disciplinaName,
    disciplinaNames,
    setDiscipline,
    setUserInfo,
  };
});
